/**
 * Client preference for recipe image size in editor/detail views.
 * Persisted in localStorage; default small (50×50 floated left of title).
 */

import { useSyncExternalStore } from 'react'

export type RecipeImageSize = 'small' | 'large'

const KEY = 'recipe-image-size'
const CHANGE_EVENT = 'recipe-image-size-change'
const DEFAULT: RecipeImageSize = 'small'

/** Fallback when localStorage is unavailable (tests / SSR). */
let memorySize: RecipeImageSize | null = null

function readStored(): string | null {
  try {
    return localStorage.getItem(KEY)
  } catch {
    return memorySize
  }
}

function parseSize(v: string | null): RecipeImageSize {
  if (v === 'large') return 'large'
  if (v === 'small') return 'small'
  return DEFAULT
}

export function getRecipeImageSize(): RecipeImageSize {
  return parseSize(readStored() ?? memorySize)
}

export function setRecipeImageSize(size: RecipeImageSize): void {
  memorySize = size
  try {
    localStorage.setItem(KEY, size)
  } catch {
    /* ignore quota / private mode — memory still updated */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: size }))
  }
}

export function toggleRecipeImageSize(): RecipeImageSize {
  const next: RecipeImageSize =
    getRecipeImageSize() === 'small' ? 'large' : 'small'
  setRecipeImageSize(next)
  return next
}

/** Same-tab + cross-tab updates. */
export function subscribeRecipeImageSize(
  cb: (size: RecipeImageSize) => void
): () => void {
  if (typeof window === 'undefined') return () => {}
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY || e.key == null) cb(getRecipeImageSize())
  }
  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent).detail
    cb(detail === 'large' || detail === 'small' ? detail : getRecipeImageSize())
  }
  window.addEventListener('storage', onStorage)
  window.addEventListener(CHANGE_EVENT, onCustom)
  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(CHANGE_EVENT, onCustom)
  }
}

export function useRecipeImageSize(): RecipeImageSize {
  return useSyncExternalStore(
    (onStoreChange) => subscribeRecipeImageSize(() => onStoreChange()),
    getRecipeImageSize,
    () => DEFAULT
  )
}
